import { Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";

export const tableContainerStyles = {
  flex: 1,
  overflow: "auto",
  borderRadius: 2,
  boxShadow: "none",
  width: "100%",
  maxWidth: { xs: "80vw", md: "90vw", lg: "100vw" },
  "& .MuiTableCell-head": {
    color: "text.primary",
    fontSize: "0.875rem",
    letterSpacing: "0.01em",
  },
} satisfies SxProps<Theme>;

export const tableHeaderCellStyles = {
  fontWeight: 600,
  backgroundColor: "background.default",
  borderBottom: (theme: Theme) => `2px solid ${theme.palette.primary.main}`,
  py: 2,
  color: "neutral1000.main",
  textAlign: "center",
} satisfies SxProps<Theme>;

export const tableRowStyles = {
  /**
   * Importante: borda por `td` deixa a linha "quebrada" por coluna.
   * Isso fica mais perceptível na coluna de ações (ícones), parecendo
   * um "risquinho" logo abaixo do conteúdo. Aqui desenhamos a borda
   * na linha (`tr`) inteira para ficar contínua.
   */
  // A separação visual entre linhas precisa existir independentemente do layout.
  // No MUI Table, a forma mais consistente é aplicar a borda nos `td`.
  "&:hover": {
    backgroundColor: "action.hover",
  },
  "& td": {
    borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  },
  "&:last-child": {
    borderBottom: 0,
  },
} satisfies SxProps<Theme>;

export const tableDataCellStyles = {
  py: 1.5,
  textAlign: "center",
} satisfies SxProps<Theme>;

export const tableActionCellStyles = {
  ...tableDataCellStyles,
  /**
   * A coluna de ações costuma ter apenas ícones (conteúdo estreito).
   * Quando a célula "encolhe" demais, a borda inferior do `td` fica visualmente
   * parecendo um "traço curto" logo abaixo dos ícones. Um minWidth + nowrap
   * deixa a coluna estável e melhora a percepção de alinhamento.
   */
  minWidth: { xs: 72, sm: 96 },
  width: { xs: 72, sm: 96 },
  whiteSpace: "nowrap",
  verticalAlign: "middle",
} satisfies SxProps<Theme>;

export const tableActionContentStyles = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  /**
   * Compatibilidade com o legado:
   * Muitos componentes `*Actions.tsx` retornam um `<TableCell>`.
   * Como o `GenericTable` já renderiza a célula de ações, isso cria um
   * `td` "aninhado" dentro do `td` externo e a borda padrão do MUI acaba
   * aparecendo como um "risquinho" embaixo dos ícones.
   *
   * Aqui neutralizamos bordas/padding de qualquer TableCell interno para que
   * a separação de linhas fique somente na linha da tabela (td externo).
   */
  "& .MuiTableCell-root": {
    borderBottom: "none !important",
    padding: 0,
    backgroundColor: "transparent",
  },
} satisfies SxProps<Theme>;

export const tableActionHeaderCellStyles = {
  ...tableHeaderCellStyles,
  minWidth: { xs: 72, sm: 96 },
  width: { xs: 72, sm: 96 },
} satisfies SxProps<Theme>;

export const tablePaginationStyles = {
  ".MuiTablePagination-select": {
    marginRight: 1,
  },
  ".MuiTablePagination-selectLabel": {
    marginRight: 1,
  },
} satisfies SxProps<Theme>;
